import { World } from '$legacyecstatic';
import { Components } from 'editor/components';
import MapboxMap from 'editor/components/map/MapboxMap';
import mapboxgl from 'mapbox-gl';
import MapboxTool, { MapboxTools } from '../MapboxTool';
import mapboxEventHandlers from './mapboxEventHandlers';

// NOTE: Even though this is in the components, it should be wrapped in an Events component.
export class MapboxEvents {
  map: MapboxMap;

  world: World<Components>;

  constructor(world: World<Components>, map: MapboxMap) {
    this.world = world;
    this.map = map;
  }

  onEventsAdd(world: World<Components>): void {
    this.world = world;
    const { map } = this.map;

    if (map) {
      map.on('load', this.onLoad);

      map.on('mousedown', this.onMouseDown);
      map.on('mousemove', this.onMouseMove);
      map.on('mouseup', this.onMouseUp);

      map.on('touchstart', this.onTouchStart);
      map.on('touchmove', this.onTouchMove);
      map.on('touchend', this.onTouchEnd);

      map.on('click', this.onClick);

      map.on('render', this.onRender);

      map.on('remove', this.onRemove);
    }
  }

  onEventsRemove(): void {
    const { map } = this.map;

    if (map) {
      map.off('load', this.onLoad);

      map.off('mousedown', this.onMouseDown);
      map.off('mousemove', this.onMouseMove);
      map.off('mouseup', this.onMouseUp);

      map.off('touchstart', this.onTouchStart);
      map.off('touchmove', this.onTouchMove);
      map.off('touchend', this.onTouchEnd);

      map.off('click', this.onClick);

      map.off('render', this.onRender);

      map.off('remove', this.onRemove);
    }
  }

  onLoad = (evt: mapboxgl.MapboxEvent): void => {
    // unimplemented
  };

  // TODO: I would really like to have a way of mapping Mapbox events to tool event,
  //       but I need to figure out a way to get the correct event type to propagate
  //       through to handler. Perhaps I can use infer?
  onMouseDown = (evt: mapboxgl.MapMouseEvent): void => {
    const { map, world } = this;
    const toolComp =
      world.grab(MapboxTool)?.component || new MapboxTool(MapboxTools.Pointer);

    const toolEvents =
      mapboxEventHandlers[toolComp.tool as keyof typeof MapboxTools];

    if (toolEvents.mousedown) {
      toolEvents.mousedown({ evt, map, world });
    }
  };

  onMouseMove = (evt: mapboxgl.MapMouseEvent): void => {
    const { map, world } = this;
    const toolComp =
      world.grab(MapboxTool)?.component || new MapboxTool(MapboxTools.Pointer);

    const toolEvents =
      mapboxEventHandlers[toolComp.tool as keyof typeof MapboxTools];

    if (toolEvents.mousemove) {
      toolEvents.mousemove({ evt, map, world });
    }
  };

  onMouseUp = (evt: mapboxgl.MapMouseEvent): void => {
    const { map, world } = this;
    const toolComp =
      world.grab(MapboxTool)?.component || new MapboxTool(MapboxTools.Pointer);

    const toolEvents =
      mapboxEventHandlers[toolComp.tool as keyof typeof MapboxTools];

    if (toolEvents.mouseup) {
      toolEvents.mouseup({ evt, map, world });
    }
  };

  onTouchStart = (evt: mapboxgl.MapTouchEvent): void => {
    const { map, world } = this;
    const toolComp =
      world.grab(MapboxTool)?.component || new MapboxTool(MapboxTools.Pointer);

    const toolEvents =
      mapboxEventHandlers[toolComp.tool as keyof typeof MapboxTools];

    if (toolEvents.touchstart) {
      toolEvents.touchstart({ evt, map, world });
    }
  };

  onTouchMove = (evt: mapboxgl.MapTouchEvent): void => {
    const { map, world } = this;
    const toolComp =
      world.grab(MapboxTool)?.component || new MapboxTool(MapboxTools.Pointer);

    const toolEvents =
      mapboxEventHandlers[toolComp.tool as keyof typeof MapboxTools];

    if (toolEvents.touchmove) {
      toolEvents.touchmove({ evt, map, world });
    }
  };

  onTouchEnd = (evt: mapboxgl.MapTouchEvent): void => {
    const { map, world } = this;
    const toolComp =
      world.grab(MapboxTool)?.component || new MapboxTool(MapboxTools.Pointer);

    const toolEvents =
      mapboxEventHandlers[toolComp.tool as keyof typeof MapboxTools];

    if (toolEvents.touchend) {
      toolEvents.touchend({ evt, map, world });
    }
  };

  onClick = (evt: mapboxgl.MapMouseEvent): void => {
    const { map, world } = this;
    const toolComp =
      world.grab(MapboxTool)?.component || new MapboxTool(MapboxTools.Pointer);

    const toolEvents =
      mapboxEventHandlers[toolComp.tool as keyof typeof MapboxTools];

    if (toolEvents.click) {
      toolEvents.click({ evt, map, world });
    }
  };

  onRender = (evt: mapboxgl.MapMouseEvent): void => {
    this.world.systems.run();
  };

  onRemove = (evt: mapboxgl.MapMouseEvent): void => {
    this.onEventsRemove();
    // Going to assume for the time being that all events on the map get cleaned up automatically, and don't
    // need to be manually removed.
  };
}

export default MapboxEvents;
